import { LayerData, meshPickTargetsAtom, splatLoadingAtom } from '@/atoms';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { SparkRenderer, SplatFileType, SplatMesh } from '@sparkjsdev/spark';
import { useThree } from '@react-three/fiber';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const RESOURCES_ENDPOINT = import.meta.env.VITE_RESOURCES_ENDPOINT as string;

// Map a filename extension to Spark's `SplatFileType`. Spark can usually
// auto-detect from a URL, but we hand it the bytes (so we can attach
// `credentials: 'include'`), so we have to spell out the type ourselves.
const fileTypeForName = (name: string): SplatFileType | undefined => {
  const lower = name.toLowerCase();
  if (lower.endsWith('.spz')) return SplatFileType.SPZ;
  if (lower.endsWith('.ply')) return SplatFileType.PLY;
  if (lower.endsWith('.splat')) return SplatFileType.SPLAT;
  if (lower.endsWith('.ksplat')) return SplatFileType.KSPLAT;
  // PlayCanvas Self-Organizing Gaussians, single-file zipped layout.
  // Spark's own URL→type mapping uses the same `.sog` → PCSOGSZIP rule.
  if (lower.endsWith('.sog')) return SplatFileType.PCSOGSZIP;
  return undefined;
};

/**
 * Renders Gaussian-splat layers (.spz / .ply) via Spark
 * (https://sparkjs.dev). One `SparkRenderer` is added to the scene per
 * mount — it owns the splat sort/accumulator pipeline and is shared by
 * every `SplatMesh` child. Each layer becomes one `<SplatMeshNode>`
 * which fetches the file with `credentials: 'include'` (our
 * resources endpoint is cookie-auth'd) and hands the bytes to
 * `SplatMesh`.
 *
 * Z-up adjustment: splat producers (Niantic Scaniverse, gsplat tools)
 * export Y-up by convention; our scene is Z-up (see CLAUDE.md). We
 * rotate the mesh -90° around X so up matches.
 */
export const Splat = ({ layers }: { layers: LayerData[] }) => {
  // PostHog kill-switch. If the flag is explicitly false in PostHog we
  // render nothing — same pattern used elsewhere (`googleStreetview`,
  // `stockpileAnalysis`, `privacyBlur`). Default-enabled when the flag
  // is missing so local dev works without configuring PostHog.
  const splatsEnabledFlag = useFeatureFlagEnabled('splats');
  const splatsEnabled = splatsEnabledFlag !== false;
  const { gl, scene } = useThree();
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));

  // One SparkRenderer per Splat mount, shared by all SplatMesh children.
  // It must live in the scene tree so its `onBeforeRender` hook fires.
  //
  // Performance knobs (defaults are tuned for "max quality at all
  // distances" which we don't need on a survey/large-scene viewer):
  //
  //   maxPixelRadius: 192
  //     Default 512. Caps the on-screen radius of a single splat. A
  //     near-camera Gaussian otherwise fills a huge fraction of the
  //     framebuffer in fragments and dominates fill cost. 192 ≈ 12 % of
  //     a 1080-tall viewport — large enough that splats still feel
  //     contiguous when you fly close, small enough that a couple of
  //     near hits don't tank FPS on a 130 MB SOG.
  //
  //   minPixelRadius: 1.5
  //     Default 0. Skip splats that project under ~1.5 px. They only
  //     contribute noise + force redundant overdraw, and at typical
  //     zoom you have hundreds of thousands of these in a SOG capture.
  //     Biggest single perf knob for "wide capture, zoomed out".
  //
  //   maxStdDev: Math.sqrt(3)
  //     Default sqrt(8) ≈ 2.83. Tighter Gaussian footprint = each splat
  //     touches fewer fragments. sqrt(3) ≈ 1.73 keeps the visible
  //     coverage but trims the long tail where opacity is < 0.05.
  const sparkRenderer = useMemo(
    () =>
      new SparkRenderer({
        renderer: gl,
        maxPixelRadius: 192,
        minPixelRadius: 1.5,
        maxStdDev: Math.sqrt(3),
      }),
    [gl],
  );
  useEffect(() => {
    if (!splatsEnabled) return;
    scene.add(sparkRenderer);
    return () => {
      scene.remove(sparkRenderer);
      sparkRenderer.dispose?.();
    };
  }, [scene, sparkRenderer, splatsEnabled]);

  if (!splatsEnabled) return null;

  return (
    <>
      {layers.map((layer) => {
        if (!layer.root) return null;
        // Respect the layer panel's visibility toggle. `layerSettings[id]`
        // is undefined while the row is still being hydrated; we treat
        // that as "not yet ready" and skip rendering, matching LandXML.tsx.
        const settings = layerSettings[layer.id];
        if (!settings) return null;
        return <SplatMeshNode key={layer.id} layer={layer} opacity={settings.modelOpacity ?? 1.0} />;
      })}
    </>
  );
};

// Y-up → Z-up rotation around X, per file type.
//
//   SPZ  : Niantic's writer normalises to Y-up, so +π/2 lands +Y on our +Z.
//   PLY  : the Inria / 3DGS training pipeline (and every viewer that grew
//          from it) stores Y-down — it inherits OpenGL screen-space
//          conventions from the original COLMAP poses. -π/2 lands it
//          right-side-up in our Z-up scene.
//   SOG  : PlayCanvas SOGS / SuperSplat encode from gsplat-training PLY
//          without re-orienting, so it inherits the same Y-down frame
//          as PLY. Same -π/2 rotation.
//   .splat / .ksplat fall through to the SPZ branch since they're
//          re-encodings of the same Y-up convention.
const xRotationForType = (fileType?: SplatFileType): number => {
  if (fileType === SplatFileType.PLY) return -Math.PI / 2;
  if (fileType === SplatFileType.PCSOGS) return -Math.PI / 2;
  if (fileType === SplatFileType.PCSOGSZIP) return -Math.PI / 2;
  return Math.PI / 2;
};

const SplatMeshNode = ({ layer, opacity }: { layer: LayerData; opacity: number }) => {
  const [mesh, setMesh] = useState<SplatMesh | null>(null);
  const fileType = layer.root ? fileTypeForName(layer.root) : undefined;
  const setLoading = useSetAtom(splatLoadingAtom);
  const setPickTargets = useSetAtom(meshPickTargetsAtom);
  // Holds the currently registered THREE.Object3D so we can also
  // unregister it from the pick-target list on cleanup. Using a ref
  // (instead of capturing in the same effect that creates the mesh)
  // keeps register/unregister symmetric across the async load.
  const pickTargetRef = useRef<THREE.Object3D | null>(null);

  useEffect(() => {
    if (!layer.root) return;
    const url = `${RESOURCES_ENDPOINT}/${layer.root}`;

    let cancelled = false;
    let createdMesh: SplatMesh | null = null;
    const abortController = new AbortController();
    const layerName = layer.name ?? 'Splat';

    // Two-phase loading state for the corner badge:
    //   1. `fetching` — bytes flowing in; we tee the response body so we
    //      can count progress without breaking Spark's stream consumer.
    //   2. `decoding` — transfer is complete but Spark is still parsing
    //      the buffer and uploading textures to the GPU. There is no
    //      progress signal during this phase (PLY/SPZ readers process
    //      the full buffer in one go), so we just show "Decoding…".
    const startLoading = (totalBytes?: number) => {
      setLoading((prev) => ({
        ...prev,
        [layer.id]: {
          layerId: layer.id,
          layerName,
          phase: 'fetching',
          progress: totalBytes ? 0 : undefined,
          loadedBytes: 0,
          totalBytes,
        },
      }));
    };
    const updateFetchProgress = (loadedBytes: number, totalBytes?: number) => {
      setLoading((prev) => {
        const cur = prev[layer.id];
        if (!cur || cur.phase !== 'fetching') return prev;
        return {
          ...prev,
          [layer.id]: {
            ...cur,
            loadedBytes,
            progress: totalBytes ? Math.min(1, loadedBytes / totalBytes) : cur.progress,
          },
        };
      });
    };
    const setDecoding = () => {
      setLoading((prev) => {
        const cur = prev[layer.id];
        if (!cur) return prev;
        return { ...prev, [layer.id]: { ...cur, phase: 'decoding', progress: 1 } };
      });
    };
    const setFailed = (error: string) => {
      setLoading((prev) => ({
        ...prev,
        [layer.id]: {
          layerId: layer.id,
          layerName,
          phase: 'failed',
          error,
        },
      }));
    };
    const clearLoading = () => {
      setLoading((prev) => {
        if (!(layer.id in prev)) return prev;
        const next = { ...prev };
        delete next[layer.id];
        return next;
      });
    };

    (async () => {
      try {
        const res = await fetch(url, { credentials: 'include', signal: abortController.signal });
        if (!res.ok) throw new Error(`splat fetch failed (${res.status}) for ${url}`);
        if (!res.body) throw new Error(`splat response has no body stream: ${url}`);
        if (cancelled) return;

        const contentLengthHeader = Number(res.headers.get('Content-Length') ?? 0);
        const totalBytes =
          Number.isFinite(contentLengthHeader) && contentLengthHeader > 0 ? contentLengthHeader : undefined;
        startLoading(totalBytes);

        // Tee the body so we can count bytes for our progress UI on one
        // branch while Spark drains the other. Without this, attaching
        // a TransformStream would consume Spark's input. `tee()` is
        // cheap — both branches share the underlying network buffer.
        const [progressBranch, sparkBranch] = res.body.tee();

        // Counter pump (fire-and-forget). We don't await it; it runs
        // alongside Spark's decode and self-completes when the wire is
        // drained.
        (async () => {
          const reader = progressBranch.getReader();
          let received = 0;
          try {
            while (!cancelled) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value) {
                received += value.byteLength;
                updateFetchProgress(received, totalBytes);
              }
            }
            if (!cancelled) setDecoding();
          } catch {
            // The fetch's AbortController error or stream cancellation
            // bubbles here; ignore — the main path handles the user-facing
            // failure state.
          } finally {
            reader.releaseLock();
          }
        })();

        const splat = new SplatMesh({
          stream: sparkBranch,
          streamLength: totalBytes,
          fileType,
          fileName: layer.root ?? undefined,
          // Keep raycastable on so the scene context-menu picker (set
          // as center, copy coordinates) works against splat surfaces
          // — the same right-click affordance offered by point clouds
          // and meshes. Spark builds an internal raycast index during
          // init; that's a small added cost we're paying deliberately.
          raycastable: true,
        });
        // Cap spherical-harmonic degree at 1. The full pipeline keeps
        // up to degree 3 (16 SH coefficients per splat, 48 floats),
        // which dominates GPU texture memory on SOG/PLY captures.
        // Degree 1 keeps the dominant view-dependent term that gives
        // splats their wet-look highlights but skips the long tail
        // that mostly costs frame time. (Field assignment because
        // SplatMesh exposes `maxSh` as a public property — the actual
        // `setMaxSh` setter lives on the inner `SplatSource`, which
        // doesn't exist yet pre-init.)
        splat.maxSh = 1;
        createdMesh = splat;
        setMesh(splat);

        // `initialized` resolves after Spark has finished parsing the
        // full file and uploaded the splat texture atlas to the GPU —
        // i.e. the moment the layer is fully rendered. Use it to clear
        // the badge.
        splat.initialized
          .then(() => {
            if (cancelled) return;
            clearLoading();
          })
          .catch((err) => {
            if (cancelled) return;
            console.error('Splat decode failed', layer.id, err);
            setFailed((err as Error)?.message ?? 'decode failed');
          });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.error('Failed to load Splat layer', layer.id, err);
        if (!cancelled) setFailed((err as Error)?.message ?? 'load failed');
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
      createdMesh?.dispose?.();
      setMesh(null);
      clearLoading();
    };
    // setLoading is a stable jotai setter; layer.name is only read for the
    // initial badge label so we deliberately don't re-run on rename.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.root]);

  // Register the splat mesh with the shared pick-target list once it's
  // mounted into the scene tree. `SceneContextMenuPicker` raycasts
  // against `meshPickTargetsAtom` on right-click and writes hits into
  // `pickedPointAtom`, so adding the splat here is what wires up
  // "set as center" / "copy coordinates" against splat surfaces.
  // SplatMesh implements `.raycast()` natively (see Spark types).
  useEffect(() => {
    if (!mesh) return;
    pickTargetRef.current = mesh as unknown as THREE.Object3D;
    setPickTargets((prev) => [...prev, mesh as unknown as THREE.Object3D]);
    return () => {
      const target = pickTargetRef.current;
      pickTargetRef.current = null;
      if (!target) return;
      setPickTargets((prev) => prev.filter((t) => t !== target));
    };
  }, [mesh, setPickTargets]);

  // Drive the SplatMesh's `opacity` field from the layer panel slider.
  // Spark blends the splat against the framebuffer at this multiplier,
  // so a value of 0 hides the layer, 1 shows it fully, and intermediate
  // values fade it. Cheaper than toggling visibility via mount/unmount.
  useEffect(() => {
    if (!mesh) return;
    mesh.opacity = opacity;
  }, [mesh, opacity]);

  if (!mesh) return null;

  return <primitive object={mesh} rotation={[xRotationForType(fileType), 0, 0]} />;
};
